import { useState, useEffect, useContext } from "react"
import { useTranslation } from 'react-i18next'
import { Divider, Grid, Box, useTheme, Typography } from "@mui/material"
import { AnchorDirection } from "../../../types/AnchorDirection"
import { useLocation, useNavigate } from "react-router-dom"
import { isNil } from 'lodash'

import { Route } from "../../../router"
import CustomDrawer from "../../atoms/CustomDrawer/CustomDrawer"
import ProfileInfo from "../ProfileInfo/ProfileInfo"
import { MembershipType } from "../../../types/MembershipType"
import MenuEntry from "../../atoms/MenuEntry/MenuEntry"
import ProfileReferralCode from "../ProfileReferralCode/ProfileReferralCode"
import { UserState } from "../../../stores/User/UserState"
import { getTemporaryCustomerPortalUrl } from "../../../clients/stripe-payment-order"
import LogoutDialog from "../LogoutDialog/LogoutDialog"
import { useSessionRepository } from "../../../repositories/Session/use-session-repository"
import { useMembershipRepository } from "../../../repositories/Membership/use-membership-repository"
import { useMembershipSettingsRepository } from "../../../repositories/MembershipSettings/use-membership-settings-repository"
import ConfirmationDialog from "../ConfirmationDialog/ConfirmationDialog"
import { MembershipStatus } from "../../../types/MembershipStatus"
import { formatToTwoDecimalPlaces, isNewCalendarYear } from "../../../helpers/utils/common"
import { useUtapRepository } from "../../../repositories/Utap/use-utap-repository"
import { UserContext } from "../../../stores/User/UserStore"
import { createMyInfoUserData } from "../../../helpers/utils/myinfoData"
import LocalStorageKeys from "../../../types/LocalStorageKeys"
import useGoogleTagManager from '../../../helpers/analytics/use-google-tag-manager'
import GtmEventName from '../../../helpers/analytics/GtmEventName'
//import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward'

import styles from "./ProfileMenu.module.scss"
import { useReferralLandingPageRepository } from "../../../repositories/ReferralLandingPage/use-referral-landing-page.repository"
import { AppContext } from "../../../stores/App/AppStore"
import Spacer from "../../atoms/Spacer/Spacer"

enum MemberShipButtonState {
    NOT_MEMBER = `NOT_MEMBER`,
    TERMINATED = `TERMINATED`,
    ACTIVE = `ACTIVE`,
}
interface ProfileMenuProps {
    expanded: boolean
    handleMenuCloseClick: () => void
    membershipType?: MembershipType
    user: UserState
}

const PLS_URL = process.env.REACT_APP_PLS_URL
const PLS_SINGPASS_CLIENT_ID = process.env.REACT_APP_PLS_SINGPASS_CLIENT_ID

const ProfileMenu = (props: ProfileMenuProps) => {
    const { expanded, handleMenuCloseClick, membershipType, user } = props

    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const { palette } = useTheme()
    const { trackButtonClick } = useGoogleTagManager()

    const { state: userState } = useContext(UserContext)
    const { state: appState } = useContext(AppContext)

    const { disposeSession, isMember } = useSessionRepository()
    const { retrieveMembership } = useMembershipRepository()
    const { getReferralPage } = useReferralLandingPageRepository()
    const { retrieveMembershipSettings } = useMembershipSettingsRepository()
    const { retrieveUtapStatus } = useUtapRepository()

    const [membershipButtonState, setMembershipButtonState] = useState(MemberShipButtonState.NOT_MEMBER)
    const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false)
    const [isCancelMembershipDialogOpen, setIsCancelMembershipDialogOpen] = useState(false)
    const [isExpanded, setIsExpanded] = useState(expanded)

    const userMembership = userState.membership?.data

    const hasUtapBalanceValueWithNric = userMembership?.nric && !isNil(userMembership?.utapBalance)
    const isDisplayTentativeUtapMessageForNewCalendarYear = isNewCalendarYear(userMembership?.utapBalanceLastUpdated)
    const tentativeLastKnownBalance = !isDisplayTentativeUtapMessageForNewCalendarYear && !isNil(userMembership?.utapBalance)

    const handleMembershipStatusChange = () => {
        if (userMembership?.status) {
            switch (userMembership.status) {
                case MembershipStatus.TERMINATED:
                    setMembershipButtonState(MemberShipButtonState.TERMINATED)
                    break
                case MembershipStatus.ACTIVE:
                case MembershipStatus.ADMIN:
                case MembershipStatus.UNION_ACTIVE:
                    setMembershipButtonState(MemberShipButtonState.ACTIVE)
                    break
            }
        }
    }

    useEffect(() => {
        isMember().then((hasMembership => {
            hasMembership ? handleMembershipStatusChange() : setMembershipButtonState(MemberShipButtonState.NOT_MEMBER)
        }))
    }, [user.session, userMembership?.status])

    useEffect(() => {
        retrieveMembership()
        retrieveMembershipSettings()
        getReferralPage()
        if (userMembership?.nric) {
            // Azure#9806 fetch utap bal only when full nric is stored
            const uinFinData = userState && createMyInfoUserData(userState)
            retrieveUtapStatus(uinFinData)
        }
    }, [])

    useEffect(() => {
        setIsExpanded(expanded)

        if (expanded) {
            localStorage.setItem(LocalStorageKeys.LAST_MENU_PAGE, location.pathname)
        }
    }, [expanded])

    const handleMembershipDraft = () => {
        handleMenuCloseClick()
        navigate(Route.MEMBERSHIP_DRAFT)
    }

    const handleMyActivityClick = () => {
        trackButtonClick(GtmEventName.VIEW_ACTIVITY_CLICK)
        handleMenuCloseClick()
        navigate(Route.PROFILE_MY_ACTIVITY)
    }

    const handleOnStripeCustomerProfileClick = () => {
        trackButtonClick(GtmEventName.MANAGE_SUBSCRIPTION_CLICK)
        handleMenuCloseClick()
        getTemporaryCustomerPortalUrl().then(url => {
            window.location.replace(url)
        })
    }

    const handleContactUsButtonClick = () => {
        trackButtonClick(GtmEventName.CONTACT_US_CLICK)
        handleMenuCloseClick()
        navigate(Route.CONTACT_US)
    }

    const handleLogOutButtonClick = () => {
        trackButtonClick(GtmEventName.LOG_OUT_CLICK)
        setIsLogoutDialogOpen(true)
    }

    const handleLogoutDialogConfirm = async (logoutFromLinkPass: boolean) => {
        disposeSession()
        if (logoutFromLinkPass && PLS_URL && PLS_SINGPASS_CLIENT_ID) {
            const logoutURL = new URL(`${PLS_URL}/logout?client_id=${encodeURIComponent(PLS_SINGPASS_CLIENT_ID)}&redirect_uri=${encodeURIComponent(window.location.origin)}`)
            window.location.replace(logoutURL)
        }
        setIsLogoutDialogOpen(false)
        handleMenuCloseClick()
        navigate(Route.ROOT)
    }

    const handleLogoutDialogDismiss = () => {
        setIsLogoutDialogOpen(false)
    }

    const handleCancelMembershipDialog = () => {
        setIsCancelMembershipDialogOpen(false)
    }

    const handleCancelMembershipButtonClick = () => {
        trackButtonClick(GtmEventName.CANCEL_MEMBERSHIP_CLICK)
        setIsCancelMembershipDialogOpen(true)
    }

    const handleAvatarClick = () => {
        handleMenuCloseClick()
    }

    const handleUnionCardClick = () => {
        trackButtonClick(GtmEventName.UNION_KNOW_MORE_CLICK)
        handleMenuCloseClick()
        navigate(`${Route.UPME_CONVERSION_BENEFITS}`)
    }

    const handleReferralCardClick = () => {
        trackButtonClick(GtmEventName.PROFILE_REFERRAL_CARD_CLICK)
        handleMenuCloseClick()
        navigate(`${Route.PROFILE_REFERRAL}`)
    }

    /* const handleLinkpointClick = () => {
        trackButtonClick(GtmEventName.LINKPOINT_BALANCE_PROFILE_CLICK)
        handleMenuCloseClick()
        navigate(`${Route.LINKPOINT_GUIDE}`)
    } */

    const handleProfileUpdateClick = () => {
        trackButtonClick(GtmEventName.UPDATE_PROFILE_CLICK)
        handleMenuCloseClick()
        navigate(`${Route.PROFILE_SUMMARY}`)
    }

    const handleVoucherWalletClick = () => {
        trackButtonClick(GtmEventName.VOUCHER_WALLET_PROFILE_CLICK)
        handleMenuCloseClick()
        navigate(`${Route.VOUCHER_WALLET}`)
    }

    let profileCardMembership = undefined
    let profileActivity = undefined
    let profileCancelMembership = undefined
    let profileStripeUserPortal = undefined
    let profileUtapBalance = undefined
    let profileUpdate = undefined
    let voucherWallet = undefined

    const displayUtapBalanceEntry = (membershipType?: MembershipType) => {
        if (!membershipType || membershipType === MembershipType.UPME) {
            return undefined
        }

        if (hasUtapBalanceValueWithNric || tentativeLastKnownBalance) {
            return (
                <Box key={t(`profile_entry_utap_balance`)}>
                    <MenuEntry entryName={t(`profile_entry_utap_balance`, { balance: formatToTwoDecimalPlaces(userMembership?.utapBalance) })} />
                </Box>
            )
        } else {
            return (
                <Box key={t(`profile_entry_utap_balance_tentative`)}>
                    <MenuEntry entryName={t(`profile_entry_utap_balance_tentative`)} />
                </Box>
            )
        }
    }

    switch (membershipButtonState) {
        case (MemberShipButtonState.NOT_MEMBER):
            break
        case (MemberShipButtonState.TERMINATED):
            profileCardMembership = (
                <Box key={t(`profile_join_again`)} onClick={() => handleMembershipDraft()}>
                    <MenuEntry entryName={t(`profile_join_again`)} />
                </Box>
            )

            profileActivity = (
                <Box key={t(`profile_entry_activity_history`)} onClick={() => handleMyActivityClick()}>
                    <MenuEntry entryName={t(`profile_entry_activity_history`)} />
                </Box>
            )
            break
        case (MemberShipButtonState.ACTIVE):
            profileUtapBalance = displayUtapBalanceEntry(userMembership?.type)

            profileUpdate = (
                <Box key={t(`profile_entry_profile_update`)} onClick={() => handleProfileUpdateClick()}>
                    <MenuEntry entryName={t(`profile_entry_profile_update`)} />
                </Box>
            )

            voucherWallet = (
                <Box key={t(`profile_entry_view_wallet`)} onClick={() => handleVoucherWalletClick()}>
                    <MenuEntry entryName={t(`profile_entry_view_wallet`)} />
                </Box>
            )

            profileActivity = (
                <Box key={t(`profile_entry_activity_history`)} onClick={() => handleMyActivityClick()}>
                    <MenuEntry entryName={t(`profile_entry_activity_history`)} />
                </Box>
            )

            if (userMembership?.hasStripeSubscription && membershipType === MembershipType.STARTER) {
                profileStripeUserPortal = (
                    <Box key={t(`profile_entry_subscription`)} onClick={() => handleOnStripeCustomerProfileClick()}>
                        <MenuEntry entryName={t(`profile_entry_subscription`)} />
                    </Box>
                )

                if (userMembership?.provisionalTerminationDateOnCancellation && !userMembership.cancelSubscriptionAtCurrentPeriod) {
                    profileCancelMembership = (
                        <Box key={t(`cancel_membership`)} onClick={() => handleCancelMembershipButtonClick()}>
                            <MenuEntry entryName={t(`cancel_membership`)} />
                        </Box>
                    )
                }
            }

            break
    }

    const warningLogOutStyle = {
        color: palette.warning.main,
    }

    const dividerColor = {
        backgroundColor: palette.disabled.main,
    }

    //const linkpointBalance = userState.linkpointPool?.balance ? formatWithThousandCommas(Number(userState.linkpointPool?.balance)) + ` ($${userState.linkpointPool.dollarValue})` : `NA`

    return (
        <CustomDrawer onClose={handleMenuCloseClick} isOpen={isExpanded} anchor={AnchorDirection.LEFT} >
            <Box>
                <Box className={styles.profileContainer}>
                    <ProfileInfo membershipType={membershipType} user={user} onAvatarClick={handleAvatarClick} onUnionCardClick={handleUnionCardClick} />
                    <Divider sx={dividerColor} />
                    <Spacer height={10} />
                    <Box className={styles.container} onClick={() => handleReferralCardClick()}>
                        <Grid container spacing={1} direction="row">
                            <Grid item xs={9} direction="column" className={styles.gridColumnContent}>
                                <Typography variant='h3' className={styles.title}>{t(`profile_entry_referral_card_title`)}</Typography>
                                <Spacer height={10} />
                                <Typography className={styles.caption}>{appState.pages.referralLandingPage.profileMenuBenefit}</Typography>
                                <Spacer height={15} />
                                <Typography className={styles.link}>{t(`profile_entry_referral_card_cta_action_text`)}</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <img className={styles.img} src={appState.pages.referralLandingPage.profileCardImage} alt={`profileCardImage`} />
                            </Grid>
                        </Grid>
                    </Box>
                    <Grid item className={styles.profileMenuEntries}>
                        {/* <Grid container direction="row" alignItems="left" spacing={1} marginLeft={`0`}>
                            <Grid display={`flex`} direction={`row`}>
                                <Typography className={styles.linkpointsBalanceTitle} variant={`h6`} onClick={handleLinkpointClick}>
                                    {t(`profile_entry_linkPoint_balance`)}
                                    <svg height={`25px`} style={{"paddingLeft":`5px`}} fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M11.118 15.343V16.5H6.2V8h1.383v7.343h3.535ZM14.955 12.333c.307 0 .577-.04.81-.118a1.59 1.59 0 0 0 .585-.331 1.38 1.38 0 0 0 .354-.525c.08-.205.119-.433.119-.685 0-.248-.04-.47-.119-.667a1.269 1.269 0 0 0-.342-.502 1.512 1.512 0 0 0-.585-.313 2.692 2.692 0 0 0-.822-.112H13.69v3.253h1.265Zm0-4.333c.556 0 1.036.065 1.442.195.41.13.747.313 1.01.549.269.232.468.513.598.844.13.326.195.689.195 1.086 0 .405-.069.777-.207 1.116a2.362 2.362 0 0 1-.615.867c-.271.24-.61.43-1.016.567-.402.134-.871.2-1.407.2H13.69V16.5h-1.383V8h2.648Z" fill="#fff"></path><path d="M22 11.996c0 5.524-4.48 9.997-9.996 9.997C6.48 22 2 17.52 2 11.997 2 6.48 6.48 2 11.996 2 17.52 2 22 6.48 22 11.996Z" fill="#FDA100"></path><path d="M12.102 14.804c.043 1.023-.62 1.708-1.777 1.8h-4.05V7.791H8.52v7.012h3.583ZM12.68 7.975c.607-.098 1.468-.183 2.667-.183 1.214 0 2.089.233 2.674.705.557.438.924 1.164.917 2.025-.007.854-.289 1.587-.811 2.067-.677.635-1.686.924-2.843.924-.261 0-.494-.014-.677-.042l-.014 3.132H12.63l.05-8.628Zm1.934 3.965c.162.035.367.05.656.05 1.05 0 1.7-.53 1.707-1.419.007-.804-.543-1.284-1.538-1.284-.388 0-.663.036-.804.064l-.021 2.589Z" fill="#fff"></path></svg>
                                </Typography>
                                <Typography className={styles.linkpointsBalance} variant={`h6`} onClick={handleLinkpointClick}>
                                    {linkpointBalance}
                                </Typography>
                            </Grid>
                            <Grid display='flex' className={styles.iconGridItem} alignItems="center" item>
                                <ArrowOutwardIcon fontSize="medium" className={styles.arrowIcon} onClick={handleLinkpointClick} />
                            </Grid>
                        </Grid> */}
                        <ProfileReferralCode user={user?.user?.data} userMembership={user?.membership?.data} />
                        {profileCardMembership}
                        {profileUtapBalance}
                        {voucherWallet}
                        {profileUpdate}
                        {profileActivity}
                        {profileCancelMembership}
                        {profileStripeUserPortal}

                        <Box key={t(`common_contact_us`)} onClick={handleContactUsButtonClick}>
                            <MenuEntry entryName={t(`common_contact_us`)} />
                        </Box>
                        <Box key={t(`profile_entry_log_out`)} onClick={handleLogOutButtonClick} sx={warningLogOutStyle}>
                            <MenuEntry entryName={t(`profile_entry_log_out`)} />
                        </Box>
                    </Grid>
                </Box>
                <ConfirmationDialog
                    confirmationTitle={t(`cancel_membership`)}
                    confirmationText={t(`cancel_membership_content`)}
                    buttonText={t(`close`)}
                    defaultOpen={isCancelMembershipDialogOpen}
                    onConfirm={handleCancelMembershipDialog}
                    acknowledgeOnly
                />
                <LogoutDialog
                    dialogText={t(`logout_message`)}
                    confirmationButtonText={t(`logout_confirm`)}
                    dismissButtonText={t(`logout_dismiss`)}
                    isOpen={isLogoutDialogOpen}
                    onDismiss={handleLogoutDialogDismiss}
                    onConfirm={handleLogoutDialogConfirm}
                />
            </Box>
        </CustomDrawer>
    )
}

export default ProfileMenu